export const isInfrontReady = (): Promise<void> => {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (typeof window.Infront !== "undefined") {
        clearInterval(interval);
        resolve();
      }
    }, 500);
  });
};

// Infront tickers are case-sensitive: Euronext Paris (feed 5475) uses mixed
// case such as "RNOp", and "RNOP" resolves to nothing. All-uppercase tickers
// are lowercased in the URL for readability; mixed-case tickers keep their
// casing so the original can be recovered by normalizeTicker().
export const tickerToSlug = (ticker: string) => {
  const slug = ticker.trim().replaceAll(" ", "_");
  return slug === slug.toUpperCase() ? slug.toLowerCase() : slug;
};

// Turns a URL segment (or a CMS ticker) into an Infront ticker. An
// all-lowercase value is a legacy/pretty slug and is uppercased; anything
// containing an uppercase letter already has its real casing.
export const normalizeTicker = (ticker: string) => {
  const value = ticker.trim().replaceAll("_", " ");
  return value === value.toLowerCase() ? value.toUpperCase() : value;
};

export const createInstrumentSlug = (feed: number | string, ticker: string) => {
  return `/bors/instrument/${feed}/${tickerToSlug(ticker)}/oversikt`;
};

export const COUNTRIES = [
  {
    displayName: "Sverige",
    slug: "sverige",
    countryCode: "SE",
    flag: "se",
    region: "SWEDEN",
    rankingChains: [
      {
        feed: 17921,
        name: "OMXS30",
        description: "OMXS30",
      },
      {
        feed: 17921,
        name: "10208",
        description: "Large Cap",
      },
      {
        feed: 17921,
        name: "10210",
        description: "Mid Cap",
      },
      {
        feed: 17921,
        name: "10212",
        description: "Small Cap",
      },
      {
        feed: 17921,
        name: "FNO_STOCK",
        description: "First North",
      },
    ],
    indexName: "OMXS30",
    tabChains: [
      { feed: 17921, name: "OMXS30", description: "OMXS30" },
      { feed: 17921, name: "10208", description: "Large Cap" },
      { feed: 17921, name: "10210", description: "Mid Cap" },
      { feed: 17921, name: "10212", description: "Small Cap" },
    ],
    initialInstrument: {
      feed: 17921,
      ticker: "OMXS30",
    },
    stockChains: [
      {
        feed: 17921,
        name: "SWEDISH_ALL_STOCKS",
        description: "Alla aktier",
      },
      {
        feed: 17921,
        name: "10208",
        description: "Large Cap Stockholm",
      },
      {
        feed: 17921,
        name: "10210",
        description: "Mid Cap Stockholm",
      },
      {
        feed: 17921,
        name: "10212",
        description: "Small Cap Stockholm",
      },
      {
        feed: 17921,
        name: "OMXS30",
        description: "OMXS30",
      },
      {
        feed: 17921,
        name: "10150",
        description: "Externa listan",
      },
      {
        feed: 17938,
        name: "NSSE",
        description: "NGM Nordic SME",
      },
      {
        feed: 17938,
        name: "EQST",
        description: "NGM Equity",
      },
      {
        feed: 17921,
        name: "FNO_STOCK",
        description: "First North Stockholm",
      },
      {
        feed: 17921,
        name: "AktieTorget-cotr",
        description: "Spotlight",
      },
      {
        feed: 17921,
        name: "SEEQ EQR",
        description: "Teckningsrätter OMX Stockholm",
      },
      {
        feed: 17921,
        name: "10094",
        description: "Teckningsrätter First North",
      },
    ],
    indicators: [
      { feed: 17921, ticker: "OMXS30" },
      { feed: 17921, ticker: "OMXSPI" },
      { feed: 12, ticker: "EURSEK" },
      { feed: 12, ticker: "USDSEK" },
      { feed: 18076, ticker: "STIF 3M GF" },
    ],
  },
  {
    displayName: "USA",
    slug: "usa",
    countryCode: "US",
    flag: "us",
    region: "USA",
    rankingChains: [
      {
        feed: 2343,
        name: "NDX",
        description: "Nasdaq 100",
      },
    ],
    indexName: "Nasdaq 100",
    tabChains: [{ feed: 2343, name: "NDX", description: "Nasdaq 100" }],
    initialInstrument: { feed: 2087, ticker: "NDX" },
    stockChains: [
      {
        feed: 2343,
        name: "NDX",
        description: "Nasdaq 100",
      },
      {
        feed: 2343,
        name: "CELS",
        description: "Nasdaq Green Energy",
      },
    ],
    indicators: [
      { feed: 2087, ticker: "NDX" },
      { feed: 2087, ticker: "COMP" },
      { feed: 12, ticker: "USDSEK" },
      { feed: 12, ticker: "USDEUR" },
      { feed: 12, ticker: "USDGBP" },
      { feed: 12, ticker: "USDJPY" },
      { feed: 12, ticker: "USDRUB" },
      { feed: 12, ticker: "USDCNY" },
      { feed: 12, ticker: "USDCAD" },
    ],
  },
  {
    displayName: "Danmark",
    slug: "danmark",
    countryCode: "DK",
    flag: "dk",
    region: "DENMARK",
    rankingChains: [
      {
        feed: 17665,
        name: "OMXC25",
        description: "OMXC25",
      },
      {
        feed: 17665,
        name: "10220",
        description: "Large Cap",
      },
      {
        feed: 17665,
        name: "10222",
        description: "Mid Cap",
      },
      {
        feed: 17665,
        name: "10224",
        description: "Small Cap",
      },
      {
        feed: 17665,
        name: "FNDK CPH",
        description: "First North",
      },
    ],
    indexName: "OMXC25",
    tabChains: [
      { feed: 17665, name: "OMXC25", description: "OMXC25" },
      { feed: 17665, name: "10220", description: "Large Cap" },
      { feed: 17665, name: "10222", description: "Mid Cap" },
      { feed: 17665, name: "10224", description: "Small Cap" },
    ],
    initialInstrument: { feed: 17665, ticker: "OMXC25" },
    stockChains: [
      {
        feed: 17665,
        name: "OMXC25",
        description: "OMXC25",
      },
      {
        feed: 17665,
        name: "10220",
        description: "Large Cap",
      },
      {
        feed: 17665,
        name: "10222",
        description: "Mid Cap",
      },
      {
        feed: 17665,
        name: "10224",
        description: "Small Cap",
      },
      {
        feed: 17665,
        name: "FNDK CPH",
        description: "First North",
      },
    ],
    indicators: [
      { feed: 17665, ticker: "OMXC25" },
      { feed: 17665, ticker: "OMXCBPI" },
      { feed: 12, ticker: "EURDKK" },
      { feed: 12, ticker: "USDDKK" },
      { feed: 12, ticker: "GBPDKK" },
      { feed: 12, ticker: "SEKDKK" },
      { feed: 12, ticker: "NOKDKK" },
      { feed: 12, ticker: "EURUSD" },
    ],
  },
  {
    displayName: "Norge",
    slug: "norge",
    countryCode: "NO",
    flag: "no",
    region: "NORWAY",
    rankingChains: [
      {
        feed: 18177,
        name: "OBX",
        description: "OBX",
      },
      {
        feed: 18177,
        name: "MATCH",
        description: "OB Match",
      },
      {
        feed: 18177,
        name: "OD",
        description: "Euronext Expand",
      },
      {
        feed: 18177,
        name: "O9",
        description: "Euronext Growth",
      },
    ],
    indexName: "OBX",
    tabChains: [
      { feed: 18177, name: "OBX", description: "OBX" },
      { feed: 18177, name: "MATCH", description: "OB Match" },
      { feed: 18177, name: "OD", description: "Euronext Expand" },
      { feed: 18177, name: "O9", description: "Euronext Growth" },
    ],
    initialInstrument: { feed: 18177, ticker: "OBX" },
    stockChains: [
      {
        feed: 18177,
        name: "OBX",
        description: "OBX",
      },
      {
        feed: 18177,
        name: "MATCH",
        description: "OB Match",
      },
      {
        feed: 18177,
        name: "OD",
        description: "Euronext Expand",
      },
      {
        feed: 18177,
        name: "O9",
        description: "Euronext Growth",
      },
    ],
    indicators: [
      { feed: 18177, ticker: "OSEBX" },
      { feed: 18177, ticker: "OBX" },
      { feed: 12, ticker: "EURNOK" },
      { feed: 12, ticker: "USDNOK" },
      { feed: 12, ticker: "GBPNOK" },
      { feed: 12, ticker: "SEKNOK" },
      { feed: 12, ticker: "DKKNOK" },
      { feed: 12, ticker: "EURUSD" },
    ],
  },
  {
    displayName: "Finland",
    slug: "finland",
    countryCode: "FI",
    flag: "fi",
    region: "FINLAND",
    rankingChains: [
      {
        feed: 100,
        name: "OMXH25",
        description: "OMXH25",
      },
      {
        feed: 100,
        name: "10196",
        description: "Large Cap",
      },
      {
        feed: 100,
        name: "10198",
        description: "Mid Cap",
      },
      {
        feed: 100,
        name: "10200",
        description: "Small Cap",
      },
      {
        feed: 100,
        name: "SEMM FN HEL",
        description: "First North",
      },
    ],
    indexName: "OMXH25",
    tabChains: [
      { feed: 100, name: "OMXH25", description: "OMXH25" },
      { feed: 100, name: "10196", description: "Large Cap" },
      { feed: 100, name: "10198", description: "Mid Cap" },
      { feed: 100, name: "10200", description: "Small Cap" },
    ],
    initialInstrument: { feed: 100, ticker: "OMXH25" },
    stockChains: [
      {
        feed: 100,
        name: "OMXH25",
        description: "OMXH25",
      },
      {
        feed: 100,
        name: "10196",
        description: "Large Cap",
      },
      {
        feed: 100,
        name: "10198",
        description: "Mid Cap",
      },
      {
        feed: 100,
        name: "10200",
        description: "Small Cap",
      },
      {
        feed: 100,
        name: "SEMM FN HEL",
        description: "First North",
      },
    ],
    indicators: [
      { feed: 100, ticker: "OMXH25" },
      { feed: 100, ticker: "OMXHPI" },
      { feed: 12, ticker: "EURSEK" },
      { feed: 12, ticker: "USDSEK" },
      { feed: 12, ticker: "EURNOK" },
      { feed: 12, ticker: "USDNOK" },
      { feed: 12, ticker: "SEKNOK" },
      { feed: 12, ticker: "EURUSD" },
    ],
  },
  {
    displayName: "Frankrike",
    slug: "frankrike",
    countryCode: "FR",
    flag: "fr",
    region: "FRANCE",
    rankingChains: [
      {
        feed: 2302,
        name: "BFR40P",
        description: "Cboe France 40",
      },
      {
        feed: 2302,
        name: "BFRM20P",
        description: "Cboe France Mid Cap 20",
      },
    ],
    indexName: "Cboe France 40",
    initialInstrument: { feed: 2302, ticker: "BFR40P" },
    stockChains: [
      {
        feed: 2302,
        name: "BFR40P",
        description: "Cboe France 40",
      },
      {
        feed: 2302,
        name: "BFRM20P",
        description: "Cboe France Mid Cap 20",
      },
    ],
    indicators: [
      { feed: 2302, ticker: "BFR40P" },
      { feed: 2302, ticker: "BFRM20P" },
      { feed: 12, ticker: "EURSEK" },
      { feed: 12, ticker: "EURUSD" },
      { feed: 12, ticker: "EURGBP" },
      { feed: 12, ticker: "EURJPY" },
      { feed: 12, ticker: "EURRUB" },
      { feed: 12, ticker: "EURCNY" },
    ],
  },
  {
    displayName: "Nederländerna",
    slug: "nederlanderna",
    countryCode: "NL",
    flag: "nl",
    region: "NETHERLANDS",
    rankingChains: [
      {
        feed: 2302,
        name: "BNL25P",
        description: "Cboe Netherlands 25",
      },
      {
        feed: 2302,
        name: "BNLM25P",
        description: "Cboe Netherlands Mid Cap 25",
      },
    ],
    indexName: "Cboe Netherlands 25",
    initialInstrument: { feed: 2302, ticker: "BNL25P" },
    stockChains: [
      {
        feed: 2302,
        name: "BNL25P",
        description: "Cboe Netherlands 25",
      },
      {
        feed: 2302,
        name: "BNLM25P",
        description: "Cboe Netherlands Mid Cap 25",
      },
    ],
    indicators: [
      { feed: 2302, ticker: "BNL25P" },
      { feed: 2302, ticker: "BNLM25P" },
      { feed: 12, ticker: "EURSEK" },
      { feed: 12, ticker: "EURUSD" },
      { feed: 12, ticker: "EURGBP" },
      { feed: 12, ticker: "EURJPY" },
      { feed: 12, ticker: "EURRUB" },
      { feed: 12, ticker: "EURCNY" },
    ],
  },
  {
    displayName: "Tyskland",
    slug: "tyskland",
    countryCode: "DE",
    flag: "de",
    region: "GERMANY",
    rankingChains: [
      {
        feed: 2302,
        name: "BDE40P",
        description: "Cboe Germany 40",
      },
      {
        feed: 2302,
        name: "BDEM50P",
        description: "Cboe Germany Mid Cap 50",
      },
      {
        feed: 2302,
        name: "BDES50P",
        description: "Cboe Germany Small Cap 50",
      },
    ],
    indexName: "Cboe Germany 40",
    initialInstrument: { feed: 2302, ticker: "BDE40P" },
    stockChains: [
      {
        feed: 2302,
        name: "BDE40P",
        description: "Cboe Germany 40",
      },
      {
        feed: 2302,
        name: "BDEM50P",
        description: "Cboe Germany Mid Cap 50",
      },
      {
        feed: 2302,
        name: "BDES50P",
        description: "Cboe Germany Small Cap 50",
      },
    ],
    indicators: [
      { feed: 2302, ticker: "BDE40P" },
      { feed: 2302, ticker: "BDEM50P" },
      { feed: 2302, ticker: "BDES50P" },
      { feed: 12, ticker: "EURSEK" },
      { feed: 12, ticker: "EURUSD" },
      { feed: 12, ticker: "EURGBP" },
      { feed: 12, ticker: "EURJPY" },
      { feed: 12, ticker: "EURRUB" },
      { feed: 12, ticker: "EURCNY" },
    ],
  },
  {
    displayName: "Storbritannien",
    slug: "storbritannien",
    countryCode: "GB",
    flag: "gb",
    rankingChains: [
      {
        feed: 2302,
        name: "Country|GB",
        description: "Cboe Europe",
      },
    ],
    indexName: "Cboe Europe 50",
    initialInstrument: { feed: 2302, ticker: "BEZ50N" },
    stockChains: [
      {
        feed: 2302,
        name: "Country|GB",
        description: "Cboe Europe",
      },
    ],
    indicators: [
      { feed: 2302, ticker: "BEZ50N" },
      { feed: 12, ticker: "GBPSEK" },
      { feed: 12, ticker: "EURGBP" },
      { feed: 12, ticker: "GBPJPY" },
    ],
  },
];

export const DEFAULT_CONTENT =
  '<div class="p-4 flex flex-col justify-center items-center space-y-2"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-8"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path></svg><p class="font-bold">Information saknas</p></div>';
